import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import { ArrowDown, ArrowUp, Plus, Save, Trash2, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { api, errorMessage, useApi } from "@/lib/api";
import { fmtDuration, MEDIA_LABELS } from "@/lib/format";
import { TRANSITIONS } from "@/lib/presets";
import type { Media, Playlist, PlaylistItem } from "@/lib/types";
import { ConfirmAction } from "@/components/app/ConfirmAction";
import { Loading, PageHeader } from "@/components/app/PageHeader";

const DEFAULT_SECONDS: Record<string, number> = { image: 10, video: 30, web: 15, text: 8, rss: 20, weather: 12, clock: 10 };
const INHERIT = "__default__";

function TransitionSelect({ value, onChange, testId }: { value?: string | null; onChange: (v: PlaylistItem["transition"]) => void; testId: string }) {
  return (
    <Select value={value || INHERIT} onValueChange={(v) => onChange(v === INHERIT ? null : (v as any))}>
      <SelectTrigger data-testid={testId} className="h-8 w-32 border-slate-700 bg-slate-950/60 text-xs"><SelectValue /></SelectTrigger>
      <SelectContent className="border-slate-800 bg-slate-900">
        <SelectItem value={INHERIT}>Transition par défaut</SelectItem>
        {TRANSITIONS.map((t) => <SelectItem key={t.v} value={t.v}>{t.label}</SelectItem>)}
      </SelectContent>
    </Select>
  );
}

function availableEntries(media: Media[]) {
  const seen = new Set<string>();
  return media.filter((m) => {
    if (!m.document_id) return true;
    if (seen.has(m.document_id)) return false;
    seen.add(m.document_id);
    return true;
  });
}

export default function PlaylistEditorPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const playlist = useApi<Playlist>(`/playlists/${id}`);
  const media = useApi<Media[]>("/media");
  const [name, setName] = useState("");
  const [items, setItems] = useState<PlaylistItem[]>([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => { if (playlist.data) { setName(playlist.data.name); setItems(playlist.data.items); } }, [playlist.data]);
  if (!playlist.data) return playlist.error ? <p className="text-rose-300">{playlist.error}</p> : <Loading />;
  const byId = Object.fromEntries((media.data || []).map((m) => [m.id, m]));
  const total = items.reduce((a, i) => a + (Number(i.seconds) || 0), 0);
  const add = (m: Media) => {
    if (!m.document_id) return setItems([...items, { media_id: m.id, seconds: DEFAULT_SECONDS[m.type] }]);
    const pages = (media.data || []).filter((x) => x.document_id === m.document_id).sort((a, b) => (a.page || 0) - (b.page || 0));
    setItems([...items, ...pages.map((p) => ({ media_id: p.id, seconds: 10 }))]);
  };

  const move = (i: number, d: number) => {
    const j = i + d;
    if (j < 0 || j >= items.length) return;
    const next = [...items];
    [next[i], next[j]] = [next[j], next[i]];
    setItems(next);
  };
  const save = async () => {
    setSaving(true);
    try {
      await api.put(`/playlists/${id}`, { name, items: items.map((i) => ({ media_id: i.media_id, seconds: Math.max(1, Number(i.seconds) || 1), transition: i.transition || null })) });
      toast.success("Liste enregistrée — les écrans concernés recevront une nouvelle version");
      playlist.reload();
    } catch (e) { toast.error(errorMessage(e)); } finally { setSaving(false); }
  };
  const remove = async () => { try { await api.delete(`/playlists/${id}`); toast.success("Liste supprimée"); navigate("/listes"); } catch (e) { toast.error(errorMessage(e)); } };

  return (
    <div>
      <PageHeader eyebrow="Éditeur de liste" title={playlist.data.name}
        actions={<>
          <ConfirmAction testId="playlist-delete" title="Supprimer cette liste ?" description="Elle sera retirée des écrans et groupes qui l'utilisent." confirmLabel="Supprimer" onConfirm={remove}
            trigger={<Button data-testid="btn-delete-playlist" variant="ghost" className="text-slate-400 hover:text-rose-300"><Trash2 className="h-4 w-4" /></Button>} />
          <Button data-testid="playlist-save-button" disabled={saving || !name.trim()} onClick={save} className="bg-blue-600 text-white hover:bg-blue-500"><Save className="mr-1 h-4 w-4" />{saving ? "Enregistrement…" : "Enregistrer"}</Button>
        </>} />
      <div className="grid gap-6 lg:grid-cols-[340px_1fr]">
        <div className="panel h-fit p-5">
          <p className="eyebrow mb-4">Médias disponibles</p>
          <div className="max-h-[560px] space-y-2 overflow-y-auto pr-1" data-testid="playlist-available-media">
            {availableEntries(media.data || []).map((m) => (
              <button key={m.id} type="button" data-testid={`playlist-add-media-${m.id}`} onClick={() => add(m)}
                className="flex w-full items-center justify-between gap-2 rounded-lg border border-slate-800 bg-slate-950/40 px-3 py-2.5 text-left text-sm transition-colors hover:border-cyan-500/40">
                <span className="min-w-0"><span className="block truncate text-slate-200">{m.document_id ? m.document_name : m.name}</span><span className="text-xs text-slate-500">{m.document_id ? `PDF · ${m.page_count} pages (toutes ajoutées)` : MEDIA_LABELS[m.type]}</span></span>
                <Plus className="h-4 w-4 flex-none text-cyan-400" />
              </button>
            ))}
            {media.data?.length === 0 && <p className="text-sm text-slate-500">Ajoutez d'abord des médias dans la médiathèque.</p>}
          </div>
        </div>
        <div className="panel p-6">
          <div className="mb-6 flex flex-col gap-4 md:flex-row md:items-end md:justify-between">
            <div className="flex-1 space-y-2"><p className="eyebrow">Nom de la liste</p><Input data-testid="playlist-editor-name" value={name} onChange={(e) => setName(e.target.value)} className="max-w-md border-slate-700 bg-slate-950/60" /></div>
            <div className="text-right"><p className="eyebrow">Durée de la boucle</p><p data-testid="playlist-total-duration" className="font-mono text-2xl text-cyan-300">{fmtDuration(total)}</p></div>
          </div>
          <ol className="space-y-2" data-testid="playlist-items">
            {items.map((it, i) => {
              const m = byId[it.media_id];
              return (
                <li key={`${it.media_id}-${i}`} data-testid={`playlist-item-${i}`} className="flex flex-wrap items-center gap-3 rounded-lg border border-slate-800 bg-slate-950/40 px-3 py-2">
                  <span className="w-6 font-mono text-xs text-slate-600">{String(i + 1).padStart(2, "0")}</span>
                  <div className="min-w-0 flex-1"><p className="truncate text-sm text-slate-200">{m?.name || "Média supprimé"}</p><p className="text-xs text-slate-500">{m ? MEDIA_LABELS[m.type] : ""}</p></div>
                  <div className="flex items-center gap-1"><Input type="number" min={1} data-testid={`playlist-item-${i}-seconds`} value={it.seconds} onChange={(e) => setItems(items.map((x, j) => (j === i ? { ...x, seconds: Number(e.target.value) } : x)))} className="h-8 w-20 border-slate-700 bg-slate-950/60 font-mono" /><span className="text-xs text-slate-500">s</span></div>
                  <TransitionSelect value={it.transition} onChange={(t) => setItems(items.map((x, j) => (j === i ? { ...x, transition: t } : x)))} testId={`playlist-item-${i}-transition`} />
                  <Button variant="ghost" size="icon" className="h-8 w-8 text-slate-500" data-testid={`playlist-item-${i}-up`} onClick={() => move(i, -1)}><ArrowUp className="h-4 w-4" /></Button>
                  <Button variant="ghost" size="icon" className="h-8 w-8 text-slate-500" data-testid={`playlist-item-${i}-down`} onClick={() => move(i, 1)}><ArrowDown className="h-4 w-4" /></Button>
                  <Button variant="ghost" size="icon" className="h-8 w-8 text-slate-500 hover:text-rose-400" data-testid={`playlist-item-${i}-remove`} onClick={() => setItems(items.filter((_, j) => j !== i))}><X className="h-4 w-4" /></Button>
                </li>
              );
            })}
            {!items.length && <p className="rounded-lg border border-dashed border-slate-800 p-8 text-center text-sm text-slate-500">Cliquez sur un média à gauche pour l'ajouter à la liste.</p>}
          </ol>
        </div>
      </div>
    </div>
  );
}
